class MCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.rootUrl = window.Shopify.routes.root;

    this.handleClearCartClick = this.handleClearCartClick.bind(this);
    this.handleClearCartKeydown = this.handleClearCartKeydown.bind(this);

    this.setHeaderCartIconAccessibility();

    if (this.cartDrawerCloseIcon) {
      this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    }

    this.addEventListener("click", this.handleClearCartClick);

    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });

    document.addEventListener("keydown", this.handleClearCartKeydown);
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (!this.cartDrawerInner) return;

    /*
      Important cleanup:
      If the customer clears cart, then adds another item without refreshing,
      stale clear-cart classes/modals can otherwise remain and block clicks.
    */
    this.resetClearCartUi();

    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.classList.add("m-cart-drawer--active");

    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });

    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    if (!this.cartDrawerInner) return;

    this.closeClearCartConfirm(true);
    this.resetClearCartUi();

    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");

    /*
      Clear every possible open flag.
      This matters because insurance-bridge.js can add .open, [open],
      and aria-hidden="false" while keeping the drawer open during insurance refresh.
    */
    this.classList.remove("m-cart-drawer--active");
    this.classList.remove("active");
    this.classList.remove("open");
    this.removeAttribute("open");
    this.setAttribute("aria-hidden", "true");

    document.documentElement.classList.remove("prevent-scroll");

    document.body.classList.remove("ca-cart-drawer-open");
    document.documentElement.classList.remove("ca-cart-drawer-open");
    document.body.classList.remove("m-cart-drawer-open");
    document.documentElement.classList.remove("m-cart-drawer-open");

    const backdrop = document.querySelector("[data-ca-cart-drawer-backdrop]");

    if (backdrop) {
      backdrop.style.pointerEvents = "";
      backdrop.style.opacity = "";
      backdrop.style.visibility = "";
    }

    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      this.classList.remove("active");
      this.classList.remove("open");
      this.removeAttribute("open");
      this.setAttribute("aria-hidden", "true");

      document.body.classList.remove("ca-cart-drawer-open");
      document.documentElement.classList.remove("ca-cart-drawer-open");
      document.body.classList.remove("m-cart-drawer-open");
      document.documentElement.classList.remove("m-cart-drawer-open");
    }, 300);
  }

renderContents(parsedState) {
  if (!parsedState || !parsedState.sections) return;

  /*
    Remember whether the drawer was already open.

    Adds made from inside the cart drawer should keep it open, while adds made
    from collection/product pages should refresh silently without opening it.
  */
  const drawerWasOpen = this.isDrawerOpen();

  this.productId = parsedState.id;

  this.getSectionsToRender().forEach((section) => {
    const sectionElement = this.getSectionElement(section);
    const sectionHTML = parsedState.sections[section.id];

    if (sectionElement && sectionHTML) {
      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    }
  });

  this.resetClearCartUi();
  this.scheduleCustomCartSync();

  if (typeof parsedState.item_count === "number") {
    this.updateCartCount(parsedState.item_count);
  } else {
    this.getCart()
      .then((cart) => {
        this.updateCartCount(cart.item_count);
      })
      .catch(() => {});
  }

  /*
    A parsedState.id indicates a successful product add.

    Do not force the drawer open. Instead, briefly animate the cart icon and
    show a compact confirmation when the drawer was previously closed.
  */
  if (parsedState.id && !drawerWasOpen) {
    this.showAddedToCartConfirmation();
  }
}

isDrawerOpen() {
  return (
    this.classList.contains("m-cart-drawer--active") ||
    this.classList.contains("active") ||
    this.classList.contains("open") ||
    this.hasAttribute("open") ||
    this.getAttribute("aria-hidden") === "false"
  );
}

animateCartIcons() {
  const cartIcons = document.querySelectorAll(".m-cart-icon-bubble");

  cartIcons.forEach((cartIcon) => {
    cartIcon.classList.remove("ca-cart-added-pulse");

    /*
      Force the browser to recognize the class removal so the animation can
      restart when several products are added quickly.
    */
    void cartIcon.offsetWidth;

    cartIcon.classList.add("ca-cart-added-pulse");

    window.setTimeout(() => {
      cartIcon.classList.remove("ca-cart-added-pulse");
    }, 700);
  });
}

showAddedToCartConfirmation() {
  this.animateCartIcons();

  let toast = document.querySelector("[data-ca-cart-added-toast]");

  if (!toast) {
    toast = document.createElement("div");
    toast.className = "ca-cart-added-toast";
    toast.setAttribute("data-ca-cart-added-toast", "");
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");

    toast.innerHTML = `
      <div class="ca-cart-added-toast__icon" aria-hidden="true">
        <svg viewBox="0 0 20 20">
          <path d="M4 10.5 8 14l8-9"></path>
        </svg>
      </div>

      <div class="ca-cart-added-toast__copy">
        <strong>Added to cart</strong>
        <span>You can keep shopping.</span>
      </div>

      <button
        type="button"
        class="ca-cart-added-toast__view"
        data-ca-cart-added-view
      >
        View cart
      </button>
    `;

    document.body.appendChild(toast);

    toast
      .querySelector("[data-ca-cart-added-view]")
      ?.addEventListener("click", () => {
        this.hideAddedToCartConfirmation();
        this.open();
      });
  }

  window.clearTimeout(this.caAddedToastTimer);

  toast.classList.remove("is-visible");

  /*
    Restart the entrance animation when several products are added quickly.
  */
  void toast.offsetWidth;

  toast.classList.add("is-visible");

  this.caAddedToastTimer = window.setTimeout(() => {
    this.hideAddedToCartConfirmation();
  }, 2600);
}

hideAddedToCartConfirmation() {
  const toast = document.querySelector("[data-ca-cart-added-toast]");

  if (toast) {
    toast.classList.remove("is-visible");
  }
}

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");

    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.textContent = "0";
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block !== "cart-items" && !updateFooter) return;

          const sectionElement = this.getSectionElement(section);

          if (sectionElement) {
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          }
        });

        this.resetClearCartUi();
        this.scheduleCustomCartSync();
      })
      .catch((error) => {
        console.error(error);
      });

    this.getCart().then((cart) => {
      this.updateCartCount(cart.item_count);

      /*
        Do not use cart.item_count to decide m-cart--empty here.
        cart.item_count includes add-ons like shipping box / protection.
        The custom state sync uses /cart.js and counts real customer items only.
      */
      this.scheduleCustomCartSync();
    });
  }

  getSectionElement(section) {
    return section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const sectionElement = parsedHTML.querySelector(selector);

    return sectionElement ? sectionElement.innerHTML : "";
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  scheduleCustomCartSync() {
    if (window.CA_SYNC_CART_DRAWER_STATE) {
      window.CA_SYNC_CART_DRAWER_STATE(0);
      window.CA_SYNC_CART_DRAWER_STATE(350);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getClearCartConfirm() {
    return this.querySelector("[data-ca-clear-cart-confirm]");
  }

  getClearCartTrigger() {
    return this.querySelector("[data-ca-clear-cart-trigger]");
  }

  resetClearCartUi() {
    const cartItems = this.querySelector("[data-minimog-cart-items]");
    const confirm = this.getClearCartConfirm();

    if (cartItems) {
      cartItems.classList.remove("ca-cart-clearing");
    }

    if (!confirm) return;

    confirm.classList.remove("is-active");
    confirm.classList.remove("is-loading");
    confirm.setAttribute("aria-hidden", "true");

    const confirmButton = confirm.querySelector("[data-ca-clear-cart-confirm-action]");
    const cancelButtons = confirm.querySelectorAll("[data-ca-clear-cart-cancel]");

    if (confirmButton) {
      confirmButton.disabled = false;
      confirmButton.removeAttribute("aria-busy");
      confirmButton.textContent = "Clear cart";
    }

    cancelButtons.forEach((button) => {
      if (button.tagName && button.tagName.toLowerCase() === "button") {
        button.disabled = false;
      }
    });
  }

  openClearCartConfirm() {
    const confirm = this.getClearCartConfirm();

    if (!confirm) return;

    confirm.classList.add("is-active");
    confirm.setAttribute("aria-hidden", "false");

    window.setTimeout(() => {
      const confirmButton = confirm.querySelector("[data-ca-clear-cart-confirm-action]");
      if (confirmButton) confirmButton.focus();
    }, 80);
  }

  closeClearCartConfirm(force = false) {
    const confirm = this.getClearCartConfirm();

    if (!confirm) return;

    if (!force && confirm.classList.contains("is-loading")) return;

    confirm.classList.remove("is-active");
    confirm.classList.remove("is-loading");
    confirm.setAttribute("aria-hidden", "true");

    this.setClearCartLoading(false);

    const trigger = this.getClearCartTrigger();
    if (trigger && document.body.contains(trigger)) {
      trigger.focus();
    }
  }

  setClearCartLoading(isLoading) {
    const confirm = this.getClearCartConfirm();

    if (!confirm) return;

    const confirmButton = confirm.querySelector("[data-ca-clear-cart-confirm-action]");
    const cancelButtons = confirm.querySelectorAll("[data-ca-clear-cart-cancel]");

    confirm.classList.toggle("is-loading", isLoading);

    if (confirmButton) {
      confirmButton.disabled = isLoading;
      confirmButton.setAttribute("aria-busy", String(isLoading));
      confirmButton.textContent = isLoading ? "Clearing..." : "Clear cart";
    }

    cancelButtons.forEach((button) => {
      if (button.tagName && button.tagName.toLowerCase() === "button") {
        button.disabled = isLoading;
      }
    });
  }

  handleClearCartKeydown(event) {
    if (event.key !== "Escape") return;

    const confirm = this.getClearCartConfirm();

    if (!confirm || !confirm.classList.contains("is-active")) return;

    event.preventDefault();
    this.closeClearCartConfirm();
  }

  handleClearCartClick(event) {
    const target = event.target;

    if (!target || !target.closest) return;

    const trigger = target.closest("[data-ca-clear-cart-trigger]");
    const cancel = target.closest("[data-ca-clear-cart-cancel]");
    const confirmAction = target.closest("[data-ca-clear-cart-confirm-action]");

    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      this.openClearCartConfirm();
      return;
    }

    if (cancel) {
      event.preventDefault();
      event.stopPropagation();
      this.closeClearCartConfirm();
      return;
    }

    if (confirmAction) {
      event.preventDefault();
      event.stopPropagation();
      this.clearCart();
    }
  }

  async refreshCartDrawerAfterClear() {
    const cartRoute =
      window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.cart
        ? window.MinimogSettings.routes.cart
        : this.rootUrl + "cart";

    const url = new URL(cartRoute, window.location.origin);
    url.searchParams.set("section_id", "cart-drawer");
    url.searchParams.set("_ca_clear_cart", Date.now().toString());

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CA-Clear-Cart": "1",
      },
    });

    if (!response.ok) {
      throw new Error("Could not refresh cart drawer.");
    }

    const responseText = await response.text();

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = this.getSectionElement(section);

      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
      }
    });

    this.resetClearCartUi();
    this.classList.add("m-cart--empty");
    this.updateCartCount(0);
    this.scheduleCustomCartSync();
  }

  dispatchClearCartEvents() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
    document.dispatchEvent(new CustomEvent("cart:change"));
    document.dispatchEvent(new CustomEvent("minimog:cart:refresh"));
    document.dispatchEvent(new CustomEvent("ca:cart:cleared"));

    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      try {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          item_count: 0,
          items: [],
          source: "ca-clear-cart",
        });
      } catch (error) {}
    }

    if (window.CA_SYNC_CART_DRAWER_STATE) {
      window.CA_SYNC_CART_DRAWER_STATE(0);
      window.CA_SYNC_CART_DRAWER_STATE(350);
      window.CA_SYNC_CART_DRAWER_STATE(900);
    }
  }

  async clearCart() {
    const confirm = this.getClearCartConfirm();
    const cartItems = this.querySelector("[data-minimog-cart-items]");

    if (confirm && confirm.classList.contains("is-loading")) return;

    this.setClearCartLoading(true);

    if (cartItems) {
      cartItems.classList.add("ca-cart-clearing");
    }

    try {
      const response = await fetch(this.rootUrl + "cart/clear.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || "Could not clear cart.");
      }

      await this.refreshCartDrawerAfterClear();

      this.resetClearCartUi();
      this.closeClearCartConfirm(true);
      this.dispatchClearCartEvents();
    } catch (error) {
      console.error("[CA Clear Cart]", error);
      alert(error.message || "Could not clear cart. Please try again.");
      this.setClearCartLoading(false);

      if (cartItems) {
        cartItems.classList.remove("ca-cart-clearing");
      }
    }
  }
}

if (!customElements.get("m-cart-drawer")) {
  customElements.define("m-cart-drawer", MCartDrawer);
}

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

if (!customElements.get("m-cart-drawer-items")) {
  customElements.define("m-cart-drawer-items", MCartDrawerItems);
}